import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { RatingBars, SentimentDonut, TopicBars, TrendChart } from '../components/charts';
import { Card, ScreenTitle, Section, Segmented, Skeleton } from '../components/ui';
import { PLATFORM_LABEL, percent, pluralize, signed } from '../lib/format';
import { mainInsight, splitTopics, verdictColor } from '../lib/insights';
import { haptic } from '../lib/telegram';

const RANGES = [
  { key: '7', label: '7 дней' },
  { key: '30', label: '30 дней' },
  { key: '90', label: '3 месяца' },
] as const;

type Metric = 'reviews' | 'rating';

/**
 * Сводка по репутации.
 *
 * Главный принцип экрана: за каждой цифрой можно провалиться в отзывы, из
 * которых она сложилась. Само по себе «обслуживание — 2.1★» не говорит, что
 * чинить; список отзывов на эту тему говорит.
 */
export function AnalyticsPage() {
  const navigate = useNavigate();
  const [days, setDays] = useState<string>('30');
  const [metric, setMetric] = useState<Metric>('reviews');

  const to = new Date();
  const from = new Date(to.getTime() - Number(days) * 86_400_000);

  const query = useQuery({
    queryKey: ['analytics', days],
    queryFn: () => api.overview({ from: from.toISOString(), to: to.toISOString() }),
  });

  /** Переход в ленту с готовым фильтром. */
  const openFeed = (params: Record<string, string>) => {
    haptic.select();
    navigate({ pathname: '/feed', search: new URLSearchParams(params).toString() });
  };

  if (query.isLoading) {
    return (
      <div className="mx-auto max-w-lg space-y-3 px-4 pt-3">
        <Skeleton className="h-24" />
        <Skeleton className="h-44" />
        <Skeleton className="h-40" />
      </div>
    );
  }

  if (!query.data) {
    return <p className="p-8 text-center text-[var(--color-hint)]">Не удалось загрузить аналитику</p>;
  }

  const data = query.data;
  const { totals, deltas, timeline, ratingDistribution, sentimentSplit, byPlatform, branches } = data;
  const insight = mainInsight(data);
  const { complaints, praise } = splitTopics(data);

  const trendPoints = timeline.map((point) => ({
    date: point.date,
    value: metric === 'reviews' ? point.reviews : point.avgRating,
  }));

  return (
    <div className="mx-auto max-w-lg pb-8">
      <ScreenTitle title="Аналитика" subtitle="Что происходит с репутацией" />

      <div className="px-4">
        <Segmented
          value={days}
          onChange={setDays}
          options={RANGES.map((range) => ({ value: range.key as string, label: range.label }))}
        />
      </div>

      {/* Вывод стоит выше графиков: он отвечает на вопрос, ради которого сюда зашли. */}
      <div className="mt-4 px-4">
        <button
          className={`pressable w-full rounded-[var(--radius-card)] bg-[var(--color-section)] p-4 text-left ${insight.filter ? '' : 'cursor-default'}`}
          style={{ borderLeft: `3px solid ${verdictColor(insight.verdict)}` }}
          disabled={!insight.filter}
          onClick={() => insight.filter && openFeed(insight.filter)}
        >
          <p className="t-headline">{insight.title}</p>
          <p className="mt-1 t-footnote leading-relaxed text-[var(--color-hint)]">{insight.detail}</p>
          {insight.filter && (
            <p className="mt-2 t-footnote font-medium text-[var(--color-accent)]">Посмотреть отзывы →</p>
          )}
        </button>
      </div>

      <div className="mt-3 mb-6 grid grid-cols-2 gap-2 px-4">
        <MetricTile
          label="Отзывов"
          value={String(totals.reviews)}
          delta={signed(deltas.reviews)}
          positive={deltas.reviews >= 0}
          onClick={() => openFeed({})}
        />
        <MetricTile
          label="Средняя оценка"
          value={totals.avgRating.toFixed(2)}
          delta={signed(deltas.avgRating, 2)}
          positive={deltas.avgRating >= 0}
          onClick={() => openFeed({ ratings: '1,2,3' })}
        />
        <MetricTile
          label="Доля ответов"
          value={percent(totals.responseRate)}
          delta={signed(deltas.responseRate * 100)}
          positive={deltas.responseRate >= 0}
          onClick={() => openFeed({ statuses: 'NEW,NEEDS_REPLY,DRAFT_READY' })}
        />
        <MetricTile
          label="Время ответа"
          value={totals.medianResponseHours === null ? '—' : `${totals.medianResponseHours.toFixed(1)} ч`}
          delta={`${percent(totals.aiRepliesShare)} — ИИ`}
          positive
        />
      </div>

      <Section
        title="Динамика"
        action={
          <div className="w-[168px]">
            <Segmented
              value={metric}
              onChange={setMetric}
              options={[
                { value: 'reviews', label: 'Отзывы' },
                { value: 'rating', label: 'Оценка' },
              ]}
            />
          </div>
        }
      >
        <Card className="mx-4">
          <TrendChart
            points={trendPoints}
            color={metric === 'rating' ? 'var(--color-positive)' : 'var(--color-accent)'}
            formatValue={(value) => (metric === 'rating' ? value.toFixed(1) : String(value))}
          />
        </Card>
      </Section>

      {complaints.length > 0 && (
        <Section title="На что жалуются" footer="Нажмите на тему — откроются отзывы, где о ней говорят.">
          <Card className="mx-4">
            <TopicBars
              data={complaints}
              tone="var(--color-negative)"
              onSelect={(topic) => openFeed({ topics: topic })}
            />
          </Card>
        </Section>
      )}

      {praise.length > 0 && (
        <Section title="За что хвалят">
          <Card className="mx-4">
            <TopicBars
              data={praise}
              tone="var(--color-positive)"
              onSelect={(topic) => openFeed({ topics: topic })}
            />
          </Card>
        </Section>
      )}

      <Section title="Оценки" footer="Нажмите на строку, чтобы прочитать эти отзывы.">
        <Card className="mx-4">
          <RatingBars data={ratingDistribution} onSelect={(rating) => openFeed({ ratings: String(rating) })} />
        </Card>
      </Section>

      <Section title="Тональность">
        <Card className="mx-4">
          <SentimentDonut data={sentimentSplit} onSelect={(sentiment) => openFeed({ sentiment })} />
        </Card>
      </Section>

      {byPlatform.length > 0 && (
        <Section title="По площадкам">
          <Card className="mx-4 p-0">
            <ul className="divide-y divide-[var(--color-separator)]">
              {byPlatform.map((item) => (
                <li key={item.platform}>
                  <button
                    className="pressable flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
                    onClick={() => openFeed({ platforms: item.platform })}
                  >
                    <span className="t-callout">{PLATFORM_LABEL[item.platform]}</span>
                    <span className="shrink-0 text-right t-footnote tabular-nums text-[var(--color-hint)]">
                      {item.reviews} отз. · {item.avgRating.toFixed(2)}★ · {percent(item.responseRate)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </Card>
        </Section>
      )}

      {branches.length > 1 && (
        <Section title="Точки" footer="Сравнение точек между собой: где просела оценка или ответы.">
          <Card className="mx-4 p-0">
            <ul className="divide-y divide-[var(--color-separator)]">
              {[...branches]
                .sort((a, b) => a.avgRating - b.avgRating)
                .map((branch) => (
                  <li key={branch.branchId}>
                    <button
                      className="pressable block w-full px-4 py-3 text-left"
                      onClick={() => openFeed({ branchIds: branch.branchId })}
                    >
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="min-w-0 flex-1 truncate t-callout">{branch.name}</span>
                        <span
                          className="shrink-0 t-subhead tabular-nums"
                          style={{ color: branch.avgRating < 4 ? 'var(--color-negative)' : undefined }}
                        >
                          {branch.avgRating.toFixed(2)}★
                        </span>
                      </div>
                      <p className="mt-0.5 t-caption text-[var(--color-hint)]">
                        {pluralize(branch.reviews, 'отзыв', 'отзыва', 'отзывов')} · отвечено{' '}
                        {percent(branch.responseRate)}
                      </p>
                    </button>
                  </li>
                ))}
            </ul>
          </Card>
        </Section>
      )}
    </div>
  );
}

function MetricTile({
  label,
  value,
  delta,
  positive,
  onClick,
}: {
  label: string;
  value: string;
  delta: string;
  positive: boolean;
  onClick?: () => void;
}) {
  const body = (
    <>
      <p className="t-caption text-[var(--color-hint)]">{label}</p>
      <p className="mt-1.5 text-[26px] leading-none font-semibold tracking-[-0.02em] tabular-nums">{value}</p>
      <p
        className="mt-1.5 t-caption"
        style={{
          color: delta === '—' ? 'var(--color-hint)' : positive ? 'var(--color-positive)' : 'var(--color-negative)',
        }}
      >
        {delta}
      </p>
    </>
  );

  if (!onClick) return <Card className="p-3.5">{body}</Card>;

  return (
    <button className="pressable rounded-[var(--radius-card)] bg-[var(--color-section)] p-3.5 text-left" onClick={onClick}>
      {body}
    </button>
  );
}
